import { template as _$template } from "solid-js/web";
import { delegateEvents as _$delegateEvents } from "solid-js/web";
import { addEventListener as _$addEventListener } from "solid-js/web";
import { use as _$use } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<div class="world-viewer__pane world-viewer__pane--map"><div class=world-viewer__canvas-host><canvas class=world-viewer__canvas></canvas></div><p class=world-viewer__hint>Drag to pan, scroll to zoom, click a dot to select a placement.`);
import { createEffect, createMemo, createSignal } from "solid-js";
import { projectAerodromes } from "../world/aerodrome-projection.ts";
import { createMapPointerHandlers } from "./world-map-controls.ts";
import { drawWorldMap } from "./world-canvas.ts";
import { createMapProjection, pickNearestPoint } from "./world-map-projection.ts";
const DEFAULT_PAN_ZOOM = {
  zoom: 1,
  panX: 0,
  panY: 0
};
/** Screen-pixel radius a click must land within to pick a placement. */
const PICK_RADIUS_PX = 10;

/** The World viewer's 2D map pane: canvas plot of every placement (coloured by object type) plus approximate aerodrome markers, with pan/zoom and click-to-select. */
export function WorldMapPane(props) {
  const [panZoom, setPanZoom] = createSignal(DEFAULT_PAN_ZOOM);
  const [viewport, setViewport] = createSignal({
    width: 1,
    height: 1
  });
  let canvasRef;
  let canvasHost;
  const aerodromes = createMemo(() => props.region ? projectAerodromes(props.region.aerodromes, props.region.bounds) : []);
  function resizeCanvas() {
    if (!canvasRef || !canvasHost) return;
    const width = canvasHost.clientWidth || 1;
    const height = canvasHost.clientHeight || 1;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvasRef.width = width * dpr;
    canvasRef.height = height * dpr;
    canvasRef.style.width = `${width}px`;
    canvasRef.style.height = `${height}px`;
    canvasRef.getContext("2d")?.setTransform(dpr, 0, 0, dpr, 0, 0);
    setViewport({
      width,
      height
    });
  }
  function pickAt(clientX, clientY) {
    const region = props.region;
    if (!region || !canvasRef) return;
    const rect = canvasRef.getBoundingClientRect();
    const projection = createMapProjection(region.bounds, viewport(), panZoom());
    const world = projection.toWorld({
      x: clientX - rect.left,
      y: clientY - rect.top
    });
    const points = region.placements.map((p, index) => ({
      index,
      x: p.x,
      y: p.y
    }));
    props.onSelect(pickNearestPoint(world, points, PICK_RADIUS_PX / projection.scale)?.index);
  }
  const handlers = createMapPointerHandlers({
    getPanZoom: panZoom,
    setPanZoom: update => setPanZoom(update),
    onClick: pickAt
  });

  // Reset the view whenever a *different* region's data lands.
  createEffect(() => {
    void props.region;
    setPanZoom(DEFAULT_PAN_ZOOM);
  });
  createEffect(() => {
    const region = props.region;
    const ctx = canvasRef?.getContext("2d");
    if (!region || !ctx) return;
    drawWorldMap(ctx, {
      bounds: region.bounds,
      placements: region.placements,
      aerodromes: aerodromes()
    }, viewport(), panZoom(), props.selectedIndex);
  });
  return (() => {
    var _el$ = _tmpl$(),
      _el$2 = _el$.firstChild,
      _el$3 = _el$2.firstChild;
    _$use(el => {
      canvasHost = el;
      new ResizeObserver(resizeCanvas).observe(el);
    }, _el$2);
    _$addEventListener(_el$3, "pointerup", handlers.onPointerUp, true);
    _$addEventListener(_el$3, "pointermove", handlers.onPointerMove, true);
    _$addEventListener(_el$3, "pointerdown", handlers.onPointerDown, true);
    _$addEventListener(_el$3, "wheel", handlers.onWheel);
    var _ref$ = canvasRef;
    typeof _ref$ === "function" ? _$use(_ref$, _el$3) : canvasRef = _el$3;
    return _el$;
  })();
}
_$delegateEvents(["pointerdown", "pointermove", "pointerup"]);